import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFullscreenOverlay } from './FullscreenOverlayContext';
import type { FullscreenOverlayPortalProps } from './types';
import { logError } from '@zg-rentals/log-error';

// Module-level scroll lock management (shared across all FullscreenOverlay instances)
// Prevents reference counting bug when multiple overlays are open simultaneously
let activeOverlayCount = 0;
let storedOriginalOverflow = '';
let savedScrollPosition = 0;
const storedSiblingDisplays = new Map<HTMLElement, string>();

// Module-level overlay stack management for proper ESC key hierarchy
// Tracks overlay instances to ensure ESC only closes the topmost overlay
let overlayStack: string[] = [];

/**
 * Stack management utilities for overlay hierarchy
 * Used by FullscreenOverlayContent to implement proper ESC key behavior
 */
export const addToOverlayStack = (id: string): void => {
  overlayStack.push(id);
};

export const removeFromOverlayStack = (id: string): void => {
  overlayStack = overlayStack.filter((stackId) => stackId !== id);
};

export const isTopmostOverlay = (id: string): boolean => {
  return overlayStack.length > 0 && overlayStack[overlayStack.length - 1] === id;
};

export const hasActiveOverlays = (): boolean => {
  return overlayStack.length > 0;
};

/**
 * FullscreenOverlayPortal Component
 *
 * Renders the overlay content in a React portal outside the normal DOM hierarchy.
 * This ensures proper z-index stacking and prevents parent elements from interfering
 * with the overlay display.
 *
 * Also manages body overflow to prevent background scrolling when overlay is open.
 * Supports multiple overlays with reference counting - scroll is only unlocked when
 * the last overlay closes.
 *
 * @example
 * <FullscreenOverlay.Portal>
 *   <FullscreenOverlay.Backdrop>
 *     <FullscreenOverlay.Content>...</FullscreenOverlay.Content>
 *   </FullscreenOverlay.Backdrop>
 * </FullscreenOverlay.Portal>
 *
 * @example With custom container
 * <FullscreenOverlay.Portal containerId="custom-portal-root">
 *   ...
 * </FullscreenOverlay.Portal>
 */
export const FullscreenOverlayPortal = ({ children, containerId = 'portal-root' }: FullscreenOverlayPortalProps) => {
  const { isOpen } = useFullscreenOverlay();
  const [hasMounted, setHasMounted] = useState(false);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const isNestedRef = useRef(false);
  const prevIsOpenRef = useRef(false);

  // Capture nesting state when isOpen transitions to true (before effects increment activeOverlayCount).
  // Nested overlays on mobile need fixed positioning to appear on top of the document-flow parent overlay.
  if (isOpen && !prevIsOpenRef.current) {
    isNestedRef.current = activeOverlayCount > 0;
  }
  if (!isOpen) {
    isNestedRef.current = false;
  }
  prevIsOpenRef.current = isOpen;

  // SSR-safe mounting detection
  useEffect(() => {
    setHasMounted(true);
  }, []);

  // Get or create portal container in effect (client-only)
  useEffect(() => {
    if (!hasMounted) return;

    let container = document.getElementById(containerId);

    if (!container) {
      // Create the container if it doesn't exist
      container = document.createElement('div');
      container.id = containerId;
      document.body.appendChild(container);
    }

    if (!container) {
      // Log error if we still can't get the container
      logError({
        error: new Error(`Failed to create portal container with id: ${containerId}`),
        context: {
          component: 'FullscreenOverlayPortal',
          containerId,
        },
      });
      return;
    }

    setPortalContainer(container);
  }, [containerId, hasMounted]);

  // Reference-counted scroll management
  // Mobile: hide page siblings so spoke scrolls at document level (triggers iOS Safari chrome retraction)
  // Desktop: lock body scroll (existing fixed overlay behavior)
  useEffect(() => {
    if (!hasMounted || typeof document === 'undefined') return;

    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const previousCount = activeOverlayCount;

    if (isOpen) {
      activeOverlayCount++;
      if (previousCount === 0) {
        if (isMobile) {
          savedScrollPosition = window.scrollY;
          const portalEl = document.getElementById(containerId);
          Array.from(document.body.children).forEach((el) => {
            if (el !== portalEl && el instanceof HTMLElement) {
              storedSiblingDisplays.set(el, el.style.display);
              el.style.display = 'none';
            }
          });
          window.scrollTo(0, 0);
        } else {
          storedOriginalOverflow = document.body.style.overflow || '';
          document.body.style.overflow = 'hidden';
        }
      } else if (isMobile) {
        // Nested overlay on mobile: lock body scroll so the parent overlay doesn't scroll behind the fixed wrapper
        document.body.style.overflow = 'hidden';
      }
    }

    return () => {
      if (isOpen && hasMounted) {
        activeOverlayCount--;
        if (activeOverlayCount === 0) {
          if (isMobile) {
            storedSiblingDisplays.forEach((display, el) => {
              el.style.display = display;
            });
            storedSiblingDisplays.clear();
            document.body.style.overflow = '';
            window.scrollTo(0, savedScrollPosition);
          } else {
            document.body.style.overflow = storedOriginalOverflow;
            storedOriginalOverflow = '';
          }
        } else if (isMobile) {
          // Nested overlay closing, parent still open: restore scrolling for parent's document flow
          document.body.style.overflow = '';
        }
      }
    };
  }, [isOpen, hasMounted, containerId]);

  // Don't render anything if not mounted (SSR safety), overlay is closed, or container not ready
  if (!hasMounted || !isOpen || !portalContainer) {
    return null;
  }

  // Nested overlays need fixed positioning to appear on top of the document-flow parent overlay on mobile
  if (isNestedRef.current) {
    return createPortal(
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 600, overflow: 'auto' }}>
        {children}
      </div>,
      portalContainer,
    );
  }

  return createPortal(children, portalContainer);
};
